import React, { useEffect, useState } from "react";
import styles from "./Perfil.module.css";
import { usuarioService } from "../../services/(usuario)/UsuarioService";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import type { NivelAcesso, Usuario } from "../../types/UsuarioTypes";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";

type FormPerfil = {
  nome: string;
  login: string;
  email: string;
  nivel: string;
};

type FormSenha = {
  senhaAntiga: string;
  novaSenha: string;
  confirmarSenha: string;
};

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  const primeira = partes[0][0];
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase();
}

function formatarData(data?: string | Date | null): string {
  if (!data) return "—";
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const Perfil: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const usuarioLogado = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );

  useSessionExpirationCheck(sessionExpiration, token);

  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [niveisAcesso, setNiveisAcesso] = useState<NivelAcesso[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);

  const [editandoDados, setEditandoDados] = useState(false);
  const [formPerfil, setFormPerfil] = useState<FormPerfil>({
    nome: "",
    login: "",
    email: "",
    nivel: "",
  });
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);
  const [mensagemPerfil, setMensagemPerfil] = useState<{
    tipo: "sucesso" | "erro";
    texto: string;
  } | null>(null);

  const [formSenha, setFormSenha] = useState<FormSenha>({
    senhaAntiga: "",
    novaSenha: "",
    confirmarSenha: "",
  });
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [mensagemSenha, setMensagemSenha] = useState<{
    tipo: "sucesso" | "erro";
    texto: string;
  } | null>(null);

  useEffect(() => {
    if (!usuarioLogado?.id) return;

    setCarregando(true);
    setErroCarregamento(null);

    Promise.all([
      usuarioService.buscarPorId(usuarioLogado.id),
      usuarioService.niveisAcesso().catch(() => []),
    ])
      .then(([dadosUsuario, niveis]) => {
        setUsuario(dadosUsuario);
        setNiveisAcesso(niveis);
        setFormPerfil({
          nome: dadosUsuario.nome,
          login: dadosUsuario.login,
          email: dadosUsuario.email,
          nivel: dadosUsuario.nivel,
        });
      })
      .catch((e: Error) => setErroCarregamento(e.message))
      .finally(() => setCarregando(false));
  }, [usuarioLogado?.id]);

  function cancelarEdicaoDados() {
    if (usuario) {
      setFormPerfil({
        nome: usuario.nome,
        login: usuario.login,
        email: usuario.email,
        nivel: usuario.nivel,
      });
    }
    setMensagemPerfil(null);
    setEditandoDados(false);
  }

  async function salvarDados(e: React.FormEvent) {
    e.preventDefault();
    if (!usuario) return;

    setSalvandoPerfil(true);
    setMensagemPerfil(null);
    try {
      await usuarioService.atualizar(usuario.id, {
        ...usuario,
        nome: formPerfil.nome,
        login: formPerfil.login,
        email: formPerfil.email,
        nivel: formPerfil.nivel,
      });
      setUsuario({
        ...usuario,
        nome: formPerfil.nome,
        login: formPerfil.login,
        email: formPerfil.email,
        nivel: formPerfil.nivel,
      });
      setEditandoDados(false);
      setMensagemPerfil({
        tipo: "sucesso",
        texto: "Dados atualizados com sucesso.",
      });
    } catch (e) {
      setMensagemPerfil({ tipo: "erro", texto: (e as Error).message });
    } finally {
      setSalvandoPerfil(false);
    }
  }

  async function salvarSenha(e: React.FormEvent) {
    e.preventDefault();
    setMensagemSenha(null);

    if (formSenha.novaSenha.length < 6) {
      setMensagemSenha({
        tipo: "erro",
        texto: "A nova senha deve ter pelo menos 6 caracteres.",
      });
      return;
    }
    if (formSenha.novaSenha !== formSenha.confirmarSenha) {
      setMensagemSenha({ tipo: "erro", texto: "As senhas não coincidem." });
      return;
    }

    setSalvandoSenha(true);
    try {
      await usuarioService.alterarPropriaSenha(
        formSenha.senhaAntiga,
        formSenha.novaSenha,
      );
      setFormSenha({ senhaAntiga: "", novaSenha: "", confirmarSenha: "" });
      setMensagemSenha({
        tipo: "sucesso",
        texto: "Senha alterada com sucesso.",
      });
    } catch (e) {
      setMensagemSenha({ tipo: "erro", texto: (e as Error).message });
    } finally {
      setSalvandoSenha(false);
    }
  }

  if (carregando) {
    return (
      <div className={styles.pagina}>
        <div className={styles.estadoVazio}>Carregando perfil…</div>
      </div>
    );
  }

  if (erroCarregamento || !usuario) {
    return (
      <div className={styles.pagina}>
        <div className={styles.faixaErro}>
          {erroCarregamento ?? "Não foi possível carregar seus dados."}
        </div>
      </div>
    );
  }

  const nivelLabel =
    niveisAcesso.find((n) => n.acesso === usuario.nivel)?.nivel ??
    usuario.nivel;

  return (
    <div className={styles.pagina}>
      <header className={styles.cabecalhoPagina}>
        <p className={styles.eyebrow}>Minha conta</p>
        <h1>Meu perfil</h1>
      </header>

      <div className={styles.layout}>
        {/* ---------- Coluna resumo ---------- */}
        <aside className={styles.cardResumo}>
          <div className={styles.avatar}>{iniciais(usuario.nome)}</div>
          <h2 className={styles.nomeResumo}>{usuario.nome}</h2>
          <span className={styles.loginResumo}>@{usuario.login}</span>

          <div className={styles.badges}>
            <span className={styles.badgeNivel}>{nivelLabel}</span>
            <span className={styles.badgeStatus}>Ativo</span>
          </div>

          <dl className={styles.infoResumo}>
            <div>
              <dt>E-mail</dt>
              <dd>{usuario.email}</dd>
            </div>
            <div>
              <dt>Membro desde</dt>
              <dd>{formatarData(usuario.dataCadastro)}</dd>
            </div>
            <div>
              <dt>Última atualização</dt>
              <dd>{formatarData(usuario.dataAlteracao)}</dd>
            </div>
          </dl>
        </aside>

        {/* ---------- Coluna formulários ---------- */}
        <div className={styles.colunaFormularios}>
          {/* Dados pessoais */}
          <section className={styles.cardSecao}>
            <div className={styles.cabecalhoSecao}>
              <h3>Informações pessoais</h3>
              {!editandoDados && (
                <button
                  type="button"
                  className={styles.botaoSecundario}
                  onClick={() => setEditandoDados(true)}
                >
                  Editar
                </button>
              )}
            </div>

            {mensagemPerfil && (
              <div
                className={
                  mensagemPerfil.tipo === "sucesso"
                    ? styles.faixaSucesso
                    : styles.faixaErro
                }
              >
                {mensagemPerfil.texto}
              </div>
            )}

            <form className={styles.formulario} onSubmit={salvarDados}>
              <div className={styles.campo}>
                <label htmlFor="nome">Nome completo</label>
                <input
                  id="nome"
                  type="text"
                  value={formPerfil.nome}
                  disabled={!editandoDados}
                  onChange={(e) =>
                    setFormPerfil((f) => ({ ...f, nome: e.target.value }))
                  }
                  required
                />
              </div>

              <div className={styles.grupoDuasColunas}>
                <div className={styles.campo}>
                  <label htmlFor="login">Login</label>
                  <input
                    id="login"
                    type="text"
                    value={formPerfil.login}
                    disabled={!editandoDados}
                    onChange={(e) =>
                      setFormPerfil((f) => ({ ...f, login: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className={styles.campo}>
                  <label htmlFor="email">E-mail</label>
                  <input
                    id="email"
                    type="email"
                    value={formPerfil.email}
                    disabled={!editandoDados}
                    onChange={(e) =>
                      setFormPerfil((f) => ({ ...f, email: e.target.value }))
                    }
                    required
                  />
                </div>
              </div>

              <div className={styles.campo}>
                <label htmlFor="nivel">Nível de acesso</label>
                <select
                  id="nivel"
                  value={formPerfil.nivel}
                  disabled={!editandoDados}
                  onChange={(e) =>
                    setFormPerfil((f) => ({ ...f, nivel: e.target.value }))
                  }
                >
                  {niveisAcesso.map((n) => (
                    <option key={n.id} value={n.acesso}>
                      {n.nivel}
                    </option>
                  ))}
                </select>
                {editandoDados && (
                  <span className={styles.dicaCampo}>
                    Alterações de nível de acesso feitas por você mesmo não são
                    salvas por segurança.
                  </span>
                )}
              </div>

              {editandoDados && (
                <div className={styles.acoesFormulario}>
                  <button
                    type="button"
                    className={styles.botaoSecundario}
                    onClick={cancelarEdicaoDados}
                    disabled={salvandoPerfil}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={styles.botaoPrimario}
                    disabled={salvandoPerfil}
                  >
                    {salvandoPerfil ? "Salvando…" : "Salvar alterações"}
                  </button>
                </div>
              )}
            </form>
          </section>

          {/* Segurança */}
          <section className={styles.cardSecao}>
            <div className={styles.cabecalhoSecao}>
              <h3>Segurança</h3>
            </div>

            {mensagemSenha && (
              <div
                className={
                  mensagemSenha.tipo === "sucesso"
                    ? styles.faixaSucesso
                    : styles.faixaErro
                }
              >
                {mensagemSenha.texto}
              </div>
            )}

            <form className={styles.formulario} onSubmit={salvarSenha}>
              <div className={styles.campo}>
                <label htmlFor="senhaAntiga">Senha atual</label>
                <input
                  id="senhaAntiga"
                  type="password"
                  value={formSenha.senhaAntiga}
                  onChange={(e) =>
                    setFormSenha((f) => ({ ...f, senhaAntiga: e.target.value }))
                  }
                  required
                />
              </div>

              <div className={styles.grupoDuasColunas}>
                <div className={styles.campo}>
                  <label htmlFor="novaSenha">Nova senha</label>
                  <input
                    id="novaSenha"
                    type="password"
                    value={formSenha.novaSenha}
                    onChange={(e) =>
                      setFormSenha((f) => ({ ...f, novaSenha: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className={styles.campo}>
                  <label htmlFor="confirmarSenha">Confirmar nova senha</label>
                  <input
                    id="confirmarSenha"
                    type="password"
                    value={formSenha.confirmarSenha}
                    onChange={(e) =>
                      setFormSenha((f) => ({
                        ...f,
                        confirmarSenha: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
              </div>

              <div className={styles.acoesFormulario}>
                <button
                  type="submit"
                  className={styles.botaoPrimario}
                  disabled={salvandoSenha}
                >
                  {salvandoSenha ? "Atualizando…" : "Atualizar senha"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Perfil;
